import { useToastController } from "@tamagui/toast";
import { type RelativePathString, useGlobalSearchParams } from "expo-router";
import { useContext, useEffect, useMemo, useRef } from "react";

import {
  useAdvanceUserProgressionToNextStep,
  useGetClinicRequest,
  useGetUserProgressionById,
  useGoToPaymentSelection,
  useHandleCheckoutSuccess,
} from "@/api/generated";
import {
  ClinicRequestType,
  EncounterStartType,
  ProgressionStep,
  type UserProgressionStateModel,
} from "@/api/generated/models";
import { LayoutContext, LoadingSpinner } from "@/components";
import BreadcrumbRedirect from "@/components/BreadcrumbRedirect";
import type { ModalContentComponent } from "@/components/LayoutProvider";
import { AD_CONVERSION_EVENTS } from "@/config/adConversionEvents";
import UserProgressionFlow from "@/conversation/sidebar/UserProgression/UserProgressionFlow";
import { useAnalytics } from "@/hooks/analytics";
import { useKeepConversationsOpen } from "@/hooks/useGroupMessages";
import { usePropagatedRouter } from "@/hooks/usePropagatedRouter";
import { useSubmitAsyncClinicRequest } from "@/hooks/useSubmitAsyncClinicRequest";
import { captureException } from "@/sentry";
import { useAuthStore } from "@/store";
import { useActiveProgressionStore } from "@/store/activeProgression";
import { isClinicRequestExpiredError } from "@/utils/clinicRequestError";

const CheckoutCancelAction = {
  close: "close",
  choose_visit: "choose_visit",
} as const;
type CheckoutCancelAction =
  (typeof CheckoutCancelAction)[keyof typeof CheckoutCancelAction];

type CheckoutReturnState =
  | { status: "missing_conversation" }
  | {
      status: "redirect_to_conversation";
      conversationId: string;
      // Present only when the redirect is a fallback from a failed progression fetch; its presence
      // is what tells the effect to log before navigating away.
      fetchError?: {
        userProgressionId: string;
        isClinicRequestExpired: boolean;
      };
    }
  | { status: "waiting" }
  | {
      status: "canceling";
      cancelAction: CheckoutCancelAction;
      conversationId: string;
      userProgressionId: string;
    }
  | {
      status: "resuming";
      conversationId: string;
      sessionId: string;
      userProgressionId: string;
      progression: UserProgressionStateModel;
    };

const getCheckoutReturnState = ({
  cancelAction,
  conversationId,
  isClinicRequestSubmitReady,
  isAuthInitialized,
  isProgressionError,
  isClinicRequestExpired,
  progressionData,
  sessionId,
  success,
  userProgressionId,
}: {
  cancelAction?: string;
  conversationId?: string;
  isClinicRequestSubmitReady: boolean;
  isAuthInitialized: boolean;
  isProgressionError: boolean;
  isClinicRequestExpired: boolean;
  progressionData?: UserProgressionStateModel;
  sessionId?: string;
  success?: string;
  userProgressionId?: string;
}): CheckoutReturnState => {
  if (!conversationId) {
    return { status: "missing_conversation" };
  }

  if (success === "false") {
    if (!userProgressionId) {
      return { status: "redirect_to_conversation", conversationId };
    }
    if (
      cancelAction !== CheckoutCancelAction.close &&
      cancelAction !== CheckoutCancelAction.choose_visit
    ) {
      return { status: "redirect_to_conversation", conversationId };
    }
    if (!isAuthInitialized) {
      return { status: "waiting" };
    }
    return {
      status: "canceling",
      cancelAction,
      conversationId,
      userProgressionId,
    };
  }

  if (success !== "true" || !sessionId || !userProgressionId) {
    return { status: "redirect_to_conversation", conversationId };
  }

  if (isProgressionError) {
    return {
      status: "redirect_to_conversation",
      conversationId,
      fetchError: { userProgressionId, isClinicRequestExpired },
    };
  }

  if (!isAuthInitialized || !progressionData) {
    return { status: "waiting" };
  }

  // Wait for the clinic request so the re-opened modal submits with the right encounter type.
  if (!isClinicRequestSubmitReady) {
    return { status: "waiting" };
  }

  return {
    status: "resuming",
    conversationId,
    sessionId,
    userProgressionId,
    progression: progressionData,
  };
};

export default function Checkout() {
  const router = usePropagatedRouter();
  const { setModalContent } = useContext(LayoutContext);
  const { track } = useAnalytics();
  const { show: showToast } = useToastController();
  const keepConversationsOpen = useKeepConversationsOpen();
  const requestExpiredToast = keepConversationsOpen
    ? "This request expired. Tell me what you need and I can set up a new visit."
    : "This request expired. Please start a new chat to continue.";
  const isAuthInitialized = useAuthStore((s) => s.isAuthInitialized);
  const hasHandledCheckoutReturnRef = useRef(false);
  const {
    cancel_action: cancelAction,
    conversation_id: conversationId,
    session_id: sessionId,
    success,
    user_progression_id: userProgressionId,
  } = useGlobalSearchParams<{
    cancel_action?: string;
    conversation_id?: string;
    session_id?: string;
    success?: string;
    user_progression_id?: string;
  }>();

  const shouldFetchProgression = Boolean(
    isAuthInitialized &&
      conversationId &&
      success === "true" &&
      sessionId &&
      userProgressionId,
  );

  const {
    data: progressionData,
    error: progressionError,
    isError: isProgressionError,
  } = useGetUserProgressionById(userProgressionId ?? "", {
    query: {
      enabled: shouldFetchProgression,
      meta: { suppressErrorToast: true },
    },
  });
  const { mutateAsync: advanceToNextStep } =
    useAdvanceUserProgressionToNextStep({
      mutation: {
        meta: { suppressErrorToast: true },
      },
    });
  const { mutateAsync: goToPaymentSelection } = useGoToPaymentSelection({
    mutation: {
      meta: { suppressErrorToast: true },
    },
  });
  const { mutateAsync: handleCheckoutSuccess } = useHandleCheckoutSuccess({
    mutation: {
      meta: { suppressErrorToast: true },
    },
  });

  const clinicRequestId = progressionData?.clinic_request_id ?? undefined;

  // The clinic request's type, not the flow, says whether this terminates in an async submit.
  const { data: clinicRequest, isError: isClinicRequestError } =
    useGetClinicRequest(conversationId ?? "", clinicRequestId ?? "", {
      query: {
        enabled: Boolean(conversationId && clinicRequestId),
        meta: { suppressErrorToast: true },
      },
    });
  const isAsyncClinicRequest =
    !!clinicRequest && clinicRequest.type !== ClinicRequestType.VISIT;
  const encounterType = (clinicRequest?.type ??
    EncounterStartType.ASYNC_RX) as EncounterStartType;

  const { handleComplete, isClinicStatusFetched } = useSubmitAsyncClinicRequest(
    {
      conversationId,
      clinicRequestId,
      encounterType,
      visitType: clinicRequest?.visit_type ?? undefined,
      enabled: Boolean(
        isAsyncClinicRequest && conversationId && isAuthInitialized,
      ),
    },
  );

  // Ready once the clinic request has settled and, for async types, clinic status has been fetched.
  const clinicRequestSettled = Boolean(clinicRequest) || isClinicRequestError;
  const isClinicRequestSubmitReady =
    !clinicRequestId ||
    (clinicRequestSettled && (!isAsyncClinicRequest || isClinicStatusFetched));

  const checkoutReturnState = useMemo(
    () =>
      getCheckoutReturnState({
        cancelAction,
        conversationId,
        isClinicRequestSubmitReady,
        isAuthInitialized,
        isClinicRequestExpired: isClinicRequestExpiredError(progressionError),
        isProgressionError,
        progressionData,
        sessionId,
        success,
        userProgressionId,
      }),
    [
      cancelAction,
      conversationId,
      isClinicRequestSubmitReady,
      isAuthInitialized,
      progressionError,
      isProgressionError,
      progressionData,
      sessionId,
      success,
      userProgressionId,
    ],
  );

  useEffect(() => {
    const redirectToConversation = (conversationId: string) => {
      router.replace(`/conversation/${conversationId}` as RelativePathString);
    };

    const openProgressionModal = (progression: UserProgressionStateModel) => {
      if (!progression.current_step) return;
      setModalContent(
        (): ModalContentComponent => (props) => (
          <UserProgressionFlow
            initialProgression={progression}
            handleComplete={handleComplete}
            {...props}
          />
        ),
      );
    };

    const resolveCheckoutCancelProgression = async (state: {
      conversationId: string;
      userProgressionId: string;
    }): Promise<UserProgressionStateModel | null> => {
      try {
        return await goToPaymentSelection({
          progressionId: state.userProgressionId,
        });
      } catch (error) {
        captureException(
          "Failed to go back to payment selection after checkout cancel",
          {
            extra: {
              conversationId: state.conversationId,
              userProgressionId: state.userProgressionId,
              error,
            },
          },
        );
        return null;
      }
    };

    const resolveCheckoutResumeProgression = async (
      state: Extract<CheckoutReturnState, { status: "resuming" }>,
    ): Promise<UserProgressionStateModel | null> => {
      const currentStep = state.progression.current_step;
      const shouldAutoAdvance = currentStep === ProgressionStep.stripe_redirect;
      // Kick off the checkout-success handler so the user isn't held up by webhook latency.
      // Re-running it is safe: the checkout.session.completed webhook is the source of truth and
      // converges on the same result.
      try {
        const checkoutSuccessResponse = await handleCheckoutSuccess({
          progressionId: state.progression.id,
          data: { session_id: state.sessionId },
        });
        const paymentProperties = checkoutSuccessResponse.payment_properties;
        const clinicRequestId = state.progression.clinic_request_id;
        if (clinicRequestId && paymentProperties) {
          useActiveProgressionStore
            .getState()
            .setCheckoutPaymentProperties(clinicRequestId, paymentProperties);
        }
        track(AD_CONVERSION_EVENTS.paymentCompleted, {
          ...paymentProperties,
          $distinct_id: `payment_completed:${state.sessionId}`,
          conversation_id: state.conversationId,
          user_progression_id: state.userProgressionId,
          product_option: state.progression.metadata?.payment_product_option,
        });
        // Needed as a workaround for how marketing has set up the Ours Privacy
        // Google Ads Integration
        track(AD_CONVERSION_EVENTS.paymentCompletedAll, {
          ...paymentProperties,
          $distinct_id: `payment_completed_all:${state.sessionId}`,
          conversation_id: state.conversationId,
          user_progression_id: state.userProgressionId,
          product_option: state.progression.metadata?.payment_product_option,
        });
        track(AD_CONVERSION_EVENTS.deprecatedPaymentSuccess, {
          ...paymentProperties,
          event_context: {
            conversation_id: state.conversationId,
            user_progression_id: state.userProgressionId,
            product_option: state.progression.metadata?.payment_product_option,
          },
        });
        if (!shouldAutoAdvance) return checkoutSuccessResponse.progression;
      } catch (error) {
        captureException("Failed to handle checkout success return", {
          extra: {
            conversationId: state.conversationId,
            userProgressionId: state.userProgressionId,
            checkoutSessionId: state.sessionId,
            currentStep: state.progression.current_step,
            error,
          },
        });
        if (!shouldAutoAdvance) return state.progression;
        // The checkout-success handler didn't confirm, so send the user back to payment selection.
        return resolveCheckoutCancelProgression({
          conversationId: state.conversationId,
          userProgressionId: state.userProgressionId,
        });
      }

      // Advance to visit escalation like any other step. If it fails, drop the user into the
      // conversation — the entitlement is tracked independently (webhook), so we never re-charge
      // them by routing back to payment.
      try {
        return await advanceToNextStep({
          progressionId: state.progression.id,
          data: { step_id: currentStep },
        });
      } catch (error) {
        captureException("Failed to advance after checkout success", {
          extra: {
            conversationId: state.conversationId,
            userProgressionId: state.userProgressionId,
            checkoutSessionId: state.sessionId,
            currentStep: state.progression.current_step,
            error,
          },
        });
        return null;
      }
    };

    switch (checkoutReturnState.status) {
      case "missing_conversation":
      case "waiting":
        return;

      case "redirect_to_conversation":
        if (checkoutReturnState.fetchError) {
          if (checkoutReturnState.fetchError.isClinicRequestExpired) {
            showToast(requestExpiredToast);
          }
          captureException("Failed to fetch checkout return data", {
            extra: {
              conversationId: checkoutReturnState.conversationId,
              ...checkoutReturnState.fetchError,
            },
          });
        }
        redirectToConversation(checkoutReturnState.conversationId);
        return;

      case "canceling": {
        if (hasHandledCheckoutReturnRef.current) return;
        hasHandledCheckoutReturnRef.current = true;

        track(AD_CONVERSION_EVENTS.checkoutCanceled, {
          event_context: {
            conversation_id: checkoutReturnState.conversationId,
            user_progression_id: checkoutReturnState.userProgressionId,
            cancel_action: checkoutReturnState.cancelAction,
          },
        });

        const handleCheckoutCancel = async () => {
          const progression =
            await resolveCheckoutCancelProgression(checkoutReturnState);
          if (
            progression &&
            checkoutReturnState.cancelAction ===
              CheckoutCancelAction.choose_visit
          ) {
            openProgressionModal(progression);
          }
          redirectToConversation(checkoutReturnState.conversationId);
        };

        handleCheckoutCancel();
        return;
      }

      case "resuming": {
        if (hasHandledCheckoutReturnRef.current) return;
        hasHandledCheckoutReturnRef.current = true;

        const handleCheckoutResume = async () => {
          const progression =
            await resolveCheckoutResumeProgression(checkoutReturnState);
          if (progression) {
            openProgressionModal(progression);
          }
          redirectToConversation(checkoutReturnState.conversationId);
        };

        handleCheckoutResume();
        return;
      }
    }
  }, [
    advanceToNextStep,
    checkoutReturnState,
    goToPaymentSelection,
    handleComplete,
    handleCheckoutSuccess,
    requestExpiredToast,
    router,
    showToast,
    setModalContent,
    track,
  ]);

  if (checkoutReturnState.status === "missing_conversation") {
    return (
      <BreadcrumbRedirect
        href="/"
        reason="No checkout return conversation ID"
        context={{
          sessionId,
          success,
          userProgressionId,
        }}
      />
    );
  }

  return (
    <LoadingSpinner
      flex={1}
      size="$1000"
      alignSelf="center"
      justifyContent="center"
    />
  );
}
